import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./CreateRole.scss";

export const CreateRole: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['roles', 'common']);
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: "",
    permissionIds: [],
  });

  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [categorizedPermissions, setCategorizedPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Function to categorize permissions by the last word
  const categorizePermissions = (permissions) => {
    const categories = {};

    permissions.forEach(permission => {
      // Extract the last word from permission name (e.g., "create order" -> "order")
      const words = permission.name.toLowerCase().split(' ');
      const category = words[words.length - 1];
      const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);

      if (!categories[capitalizedCategory]) {
        categories[capitalizedCategory] = [];
      }
      categories[capitalizedCategory].push(permission);
    });

    return categories;
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await adminApi.call('/role/permissions', 'GET');
        if (!response.success) {
          showErrorToast(response.error || t('roles:messages.fetchError'));
          return;
        }
        const permissions = response.data?.items || response.data; // support both paged and unpaged
        setAvailablePermissions(permissions);
        setCategorizedPermissions(categorizePermissions(permissions));
      } catch (err) {
        console.error(err);
        showErrorToast(t('roles:messages.fetchError'));
      }
    };

    fetchPermissions();
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = t('roles:validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('roles:validation.nameMinLength');
    }

    if (formData.permissionIds.length === 0) {
      newErrors.permissions = t('roles:validation.permissionsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData((prev) => {
      const alreadySelected = prev.permissionIds.includes(permissionId);
      return {
        ...prev,
        permissionIds: alreadySelected
          ? prev.permissionIds.filter((id) => id !== permissionId)
          : [...prev.permissionIds, permissionId],
      };
    });
  };

  const submitItem = async () => {
    // Validate form before submission
    if (!validateForm()) {
      showErrorToast(t('roles:validation.fillAllFields'));
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/role/create', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('roles:messages.createError'));
        return;
      }

      showSuccessToast(t('roles:messages.createSuccess'));
      setFormData({ name: "", permissionIds: [] }); // Reset form
    } catch (error: any) {
      showErrorToast(t('roles:messages.createError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('roles:breadcrumbs.roles'), path: '/roles' },
    { label: t('roles:breadcrumbs.create') }
  ];

  return (
    <PageLayout
      title={t('roles:createRole')}
      subtitle={t('roles:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('roles:fields.name')} <span className="required">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('roles:placeholders.name')}
              required
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>
        </div>

        <div className="permissions-section">
          <label className="form-label">{t('roles:fields.permissions')} <span className="required">*</span></label>
          {errors.permissions && <div className="invalid-feedback d-block">{errors.permissions}</div>}

          {Object.keys(categorizedPermissions).length > 0 ? (
            Object.entries(categorizedPermissions).map(([category, permissions]) => (
              <div key={category} className="permission-category">
                <h5 className="category-title">
                  <i className="fa fa-folder-open me-2"></i>
                  {category}
                </h5>
                <div className="permissions-grid">
                  {(permissions as any[]).map((permission: any) => (
                    <div className="permission-item" key={permission.id}>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`perm-${permission.id}`}
                          checked={formData.permissionIds.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`perm-${permission.id}`}
                        >
                          {permission.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="loading-permissions">
              <i className="fa fa-spinner fa-spin me-2"></i>
              {t('roles:messages.loadingRoles')}
            </div>
          )}
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={submitItem}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('roles:buttons.submitting') : t('roles:buttons.submit')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
